/**
 * UserAvatar Component
 * Displays user avatar with initials and consistent colors
 * Uses UserIdentity value object for formatting
 */

import React from 'react';
import { Avatar, Tooltip } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { UserIdentity } from '@/types/user';
import { AVATAR_SIZES } from '../../constants';

interface UserAvatarProps {
  /** User data object with identity fields */
  user: {
    owner_full_name?: string | null;
    owner_email?: string | null;
    owner_username?: string | null;
    owner_id?: string | null;
    user_id?: string | null;
    created_by?: string | null;
    full_name?: string | null;
    email?: string | null;
    username?: string | null;
  };
  /** Avatar size */
  size?: 'small' | 'medium' | 'large';
  /** Custom size in pixels (overrides size prop) */
  customSize?: number;
  /** Show tooltip with user info */
  showTooltip?: boolean;
  /** Custom tooltip text */
  tooltipText?: string;
  /** Additional sx props */
  sx?: Record<string, any>;
}

const SIZE_MAP = {
  small: AVATAR_SIZES.SMALL,
  medium: AVATAR_SIZES.MEDIUM,
  large: AVATAR_SIZES.LARGE,
} as const;

/**
 * UserAvatar component displays user avatar with initials
 * Automatically handles missing data and provides consistent colors
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'medium',
  customSize,
  showTooltip = true,
  tooltipText,
  sx = {},
}) => {
  const identity = UserIdentity.fromDocument(user);
  const avatarSize = customSize || SIZE_MAP[size];

  // If no user identity data, show default icon
  if (identity.isEmpty()) {
    const avatar = (
      <Avatar
        sx={{
          bgcolor: 'action.disabled',
          width: avatarSize,
          height: avatarSize,
          fontSize: avatarSize * 0.5,
          ...sx,
        }}
      >
        <PersonIcon fontSize="small" />
      </Avatar>
    );

    return showTooltip ? (
      <Tooltip title={tooltipText || 'Unknown User'}>
        {avatar}
      </Tooltip>
    ) : avatar;
  }

  const avatar = (
    <Avatar
      sx={{
        bgcolor: identity.getAvatarColor(),
        width: avatarSize,
        height: avatarSize,
        fontSize: avatarSize * 0.4,
        fontWeight: 500,
        ...sx,
      }}
    >
      {identity.getInitials()}
    </Avatar>
  );

  return showTooltip ? (
    <Tooltip title={tooltipText || identity.getTooltipText()}>
      {avatar}
    </Tooltip>
  ) : avatar;
};

export default UserAvatar;
